// Utils
export { cn } from './lib/utils';
export {
  defaultFormatCurrency,
  denyAll,
  resolveHostCapabilities,
  useHostCapabilities,
  type CanFn,
  type FormatCurrencyFn,
  type HostCapabilities,
  type ResolvedHostCapabilities,
} from './lib/host-capabilities';

// Hooks
export { useMediaQuery, LARGE_SCREEN_QUERY } from './hooks/useMediaQuery';

// Components
export { Button, buttonVariants, type ButtonProps } from './components/ui/button';
export { Input, inputVariants, type InputProps } from './components/ui/input';
export { PasswordInput, type PasswordInputProps } from './components/ui/password-input';
export { Label, labelVariants, type LabelProps } from './components/ui/label';
export { Textarea, textareaVariants, type TextareaProps } from './components/ui/textarea';
export { Checkbox } from './components/ui/checkbox';
export {
  NativeSelect,
  nativeSelectVariants,
  type NativeSelectProps,
} from './components/ui/native-select';
export { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
export { Switch, type SwitchProps } from './components/ui/switch';
export {
  FileDropzone,
  useFileDropzone,
  fileMatchesAccept,
  acceptAttribute,
  type FileAccept,
  type FileDropzoneProps,
  type UseFileDropzoneOptions,
} from './components/ui/file-dropzone';
export { ConfirmDialog, type ConfirmDialogProps } from './components/ui/confirm-dialog';
export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from './components/ui/form';
export { Separator } from './components/ui/separator';
export { Avatar, type AvatarProps } from './components/ui/avatar';

// Molecules
export {
  UserProfileCard,
  type UserProfileCardProps,
} from './components/molecules/user-profile-card';

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './components/ui/dialog';

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './components/ui/sheet';

export {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from './components/ui/popover';

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './components/ui/table';

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';

// Sidebar components
export {
  // Shadcn-style primitives
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
  // GLP-styled sidebar
  GLPSidebar,
  GLPSidebarProvider,
  GLPSidebarNavGroup,
  GLPSidebarItem,
  useGLPSidebar,
  type NavItem,
  type UserProfile,
  // GLP-styled secondary (inner) sidebar
  GLPSecondarySidebar,
  GLPSecondarySidebarProvider,
  useGLPSecondarySidebar,
  type GLPSecondaryNavItem,
  // Mobile shell (top bar + off-canvas drawer)
  GLPMobileTopBar,
  GLPMobileNavDrawer,
  useMobileNavAutoClose,
  type UseMobileNavAutoCloseOptions,
  // Nav tree (modules + section links)
  buildNavItems,
  buildSecondaryNavItems,
  findActiveNavModule,
  findActiveNavSection,
  isNavPathActive,
  type NavMatch,
  type NavTreeLink,
  type NavTreeModule,
  // User profile widget
  GLPUserProfile,
  type GLPUserProfileUser,
  type GLPUserProfileMenuItem,
  type GLPUserProfileMenuSection,
  type GLPUserProfileProps,
} from './components/sidebar';

// Data table (ported from glp-frontend components/helpers/data-table)
export * from './components/data-table';
